/**
 * API service integrations for the Salzburg Cab Private Tours pages.
 * Implements real network requests to the backend server with standard headers and error handling.
 * Fallback local mock data is provided with a simple switch to enable/disable.
 */

export interface PrivateTour {
  id: string;
  title: string;
  subtitle: string;
  shortDescription: string;
  duration: string;
  price: number;
  image: string;
  images: string[];
  tags: string[];
  routeQuery: string;
  highlights: string[];
  excludedFeatures?: string[];
  excluded_features?: string[];
  goodToKnow: string[];

  overview?: string;
  categories?: string[];
  startLocation?: string;
  maxPeople?: number;
  active?: boolean;
  showOnHomepage?: boolean;
  itinerary?: string;
}

export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  warning?: string;
}

export const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || 'http://72.61.92.71:8000').replace(/\/$/, '');

export const USE_REAL_BACKEND = true;

export function shouldShowOnHomepage(item?: any): boolean {
  const value =
    item?.showOnHomepage ??
    item?.show_on_homepage ??
    item?.showOnHomePage;

  const showOnHomepage = typeof value === 'boolean'
    ? value
    : typeof value === 'number'
      ? value === 1
      : typeof value === 'string'
        ? ['true', '1', 'yes'].includes(value.trim().toLowerCase())
        : false;
  const activeValue = item?.active ?? item?.is_active;
  const active = typeof activeValue === 'boolean'
    ? activeValue
    : typeof activeValue === 'number'
      ? activeValue === 1
      : typeof activeValue === 'string'
        ? ['true', '1', 'yes', 'active'].includes(activeValue.trim().toLowerCase())
        : true;

  return active && showOnHomepage;
}

/** Normalize backend/mock payloads so subtitle + shortDescription always exist (day-trip parity). */
export function normalizePrivateTour(raw: Partial<PrivateTour> | Record<string, unknown>): PrivateTour {
  const data = raw as Record<string, unknown>

  const parseBooleanValue = (value: unknown): boolean | undefined => {
    if (typeof value === 'boolean') return value
    if (typeof value === 'number') return value === 1
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase()
      if (['true', '1', 'yes'].includes(normalized)) return true
      if (['false', '0', 'no'].includes(normalized)) return false
    }
    return undefined
  }

  const parseArrayField = (val: unknown): string[] => {
    if (Array.isArray(val)) {
      return val
        .map((item) => {
          if (typeof item === 'string') return item
          if (!item) return ''
          if (typeof item === 'object') {
            const objectItem = item as Record<string, unknown>

            return (
              objectItem.name ??
              objectItem.title ??
              objectItem.label ??
              objectItem.description ??
              JSON.stringify(item)
            )
          }
          return String(item)
        })
        .map((s) => s && String(s).trim())
        .filter(Boolean)
    }

    if (typeof val === 'string') {
      try {
        const parsed = JSON.parse(val)

        if (Array.isArray(parsed)) {
          return parseArrayField(parsed)
        }

        return val.split(',').map((s) => s.trim()).filter(Boolean)
      } catch (e) {
        return val.split(',').map((s) => s.trim()).filter(Boolean)
      }
    }

    return []
  }

  const categories = parseArrayField(
    data.categories ??
    data.category_list ??
    data.tags
  )

  let tags = parseArrayField(
    data.tags ??
    data.categories ??
    data.category_list
  )

  if (tags.length === 0 && categories.length > 0) {
    tags = categories
  }

  const images =
    Array.isArray(data.images) && data.images.length > 0
      ? data.images
          .map((img: unknown) =>
            typeof img === 'string'
              ? img
              : (img as Record<string, unknown>)?.image
          )
          .filter(Boolean)
      : Array.isArray(data.gallery_images) && data.gallery_images.length > 0
        ? data.gallery_images
            .map((img: unknown) =>
              typeof img === 'string'
                ? img
                : (img as Record<string, unknown>)?.image
            )
            .filter(Boolean)
        : []

  const singleImage =
    data.image ??
    data.cover_image ??
    data.thumbnail ??
    (images.length > 0 ? images[0] : '/assets/images/van-tour-1.png')

  const priceRaw =
    data.price ??
    data.basePrice ??
    data.base_price ??
    data.extraPrice ??
    data.extra_price ??
    480

  const priceNum =
    typeof priceRaw === 'number'
      ? (priceRaw > 0 ? priceRaw : 480)
      : (Number(priceRaw) || 480)

  const title =
    data.title ||
    'Private Van Tour'

  const subtitle =
    data.subtitle ||
    data.tagline ||
    data.location ||
    'Cross-Border Alpine Journey'

  const shortDescription =
    data.shortDescription ??
    data.short_description ??
    data.description ??
    data.overview ??
    subtitle

  const overview =
    data.overview ??
    data.shortDescription ??
    data.short_description ??
    data.description ??
    ''
  const itinerary = data.itinerary ?? '';
const duration = (() => {
  const durationText = String(
    data.duration ??
    data.time ??
    ''
  )

  const durationNumbers =
    durationText.match(/\d+/g)?.map(Number) ?? []

  const days = Number(
    data.durationDays ??
    data.duration_days ??
    (
      Number.isFinite(Number(data.duration))
        ? data.duration
        : durationNumbers[0]
    ) ??
    7
  )

  const nights = Number(
    data.durationNights ??
    data.duration_nights ??
    durationNumbers[1] ??
    6
  )

  return `${days} Day${days === 1 ? '' : 's'} • ${nights} Night${nights === 1 ? '' : 's'}`
})()

  const routeQuery =
    data.routeQuery ??
    data.route_query ??
    data.startLocation ??
    data.start_location ??
    ''

  const startLocation =
    data.startLocation ??
    data.start_location ??
    'Salzburg'

  const highlights =
    parseArrayField(
      data.highlights ??
      data.highlights_list ??
      data.features
    )

  const excludedFeatures =
    parseArrayField(
      data.excludedFeatures ??
      data.excluded_features ??
      data.excluded_features_rel
    )

  const goodToKnow =
    parseArrayField(
      data.goodToKnow ??
      data.good_to_know ??
      data.good_to_knows
    )

  const maxPeople =
    Number(
      data.maxPeople ??
      data.max_people ??
      data.people ??
      8
    ) || 8

  const active =
    parseBooleanValue(data.active) ??
    parseBooleanValue(data.is_active) ??
    true

  const showOnHomepage =
    parseBooleanValue(data.showOnHomepage) ??
    parseBooleanValue(data.show_on_homepage) ??
    false

  return {
    id: String(data.id ?? ''),
    title: String(title),
    subtitle: String(subtitle),
    shortDescription: String(shortDescription),
    duration,
    price: priceNum,
    image: String(
      singleImage ||
      '/assets/images/van-tour-1.png'
    ),
    images:
      images.length > 0
        ? images as string[]
        : [
            String(
              singleImage ||
              '/assets/images/van-tour-1.png'
            )
          ],
    tags:
      tags.length > 0
        ? tags
        : ['Sightseeing', 'Transfers'],
    routeQuery: String(routeQuery),
    highlights,
    excludedFeatures,
    excluded_features: excludedFeatures,
    goodToKnow,
    overview: String(overview),
    itinerary: String(itinerary),
    categories:
      categories.length > 0
        ? categories
        : ['Sightseeing', 'Transfers'],
    startLocation: String(startLocation),
    maxPeople,
    active,
    showOnHomepage,
  }
}

export async function apiGetPrivateTours(): Promise<ApiResponse<PrivateTour[]>> {
  try {
    const url = `${API_BASE_URL}/api/admin/private-tours/`
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    let result = null
    try {
      result = await response.json()
    } catch (err) {
      // keep result null
    }

    // Debug: log network info to browser console for easier troubleshooting
    // eslint-disable-next-line no-console
    console.debug(
      '[apiGetPrivateTours] url=',
      url,
      'status=',
      response.status,
      'ok=',
      response.ok,
      'result=',
      result
    )

    if (!response.ok) {
      return {
        success: false,
        message: result?.message || 'Failed to fetch private tours.'
      };
    }

    // Backend admin API returns a paginated object with `results`.
    // Normalize to return a typed array when possible.
    const rawData = Array.isArray(result?.results)
      ? result.results
      : result?.data || result

    const data = Array.isArray(rawData)
      ? rawData.map(normalizePrivateTour)
      : []

    return {
      success: true,
      message: result?.message || 'Private tours loaded successfully.',
      data,
    };
  } catch (error) {
    return {
      success: false,
      message: 'Connection failed.',
      warning: `Unable to reach API at ${API_BASE_URL}. Falling back to mock data could be configured.`
    };
  }
}

export async function apiGetPrivateTourById(id: string): Promise<ApiResponse<PrivateTour>> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/admin/private-tours/${id}/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    const result = await response.json().catch(() => null);

    if (!response.ok) {
      return {
        success: false,
        message: result?.message || `Failed to fetch tour with id ${id}.`
      };
    }

    const raw = result?.data || result

    return {
      success: true,
      message: result?.message || 'Tour details loaded successfully.',
      data: normalizePrivateTour(raw),
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[apiGetPrivateTours] network error', error)

    return {
      success: false,
      message: 'Connection failed.',
      warning: `Unable to reach API at ${API_BASE_URL}.`
    };
  }
}